import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Settings } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/features/shadcn/ui/dialog'
import { Label } from '@/features/shadcn/ui/label'
import { RadioGroup, RadioGroupItem } from '@/features/shadcn/ui/radio-group'
import { Button } from '@/features/shadcn/ui/button'
import { BsGearFill } from 'react-icons/bs'
import ReactTooltip from 'react-tooltip'
import {
  setSettingsModalOpen,
  selectAttributionSettings,
  selectAdPlatformOptions,
  setAttributionSetting,
  selectSettingsModalOpen,
  saveTrafficSettings,
  loadTrafficSettings,
  selectIsSaving,
  selectTrafficSettingsError,
  selectSaveStatus,
  selectIsInitialized,
  resetSaveStatus,
  selectMetricSettings,
  setMetricSetting,
} from '../trafficSettingSlice'
import { logoDict } from '../utils'
import _ from 'lodash'

const AdsSettings = ({ adPlatformOptions, handleAttributionChange, isSaving, attributionSettings }) => {
  return (
    <div className="tw-space-y-3">
      {adPlatformOptions
        ?.filter((platform) => ['google_ads', 'klaviyo', 'facebook_ads'].includes(platform.value))
        .map((platform) => {
          const currentAttribution = attributionSettings[platform.value] || 'first'
          const imgSrc = logoDict[platform.value]?.path
          const imgSize = logoDict[platform.value]?.size
          const showTitle = logoDict[platform.value]?.title

          return (
            <div key={platform.value} className="tw-bg-gray-50 tw-rounded-lg tw-p-3">
              {/* Platform Header */}
              <div className="tw-flex tw-items-center tw-gap-2 tw-mb-3">
                <img src={imgSrc} alt="Logo" className={imgSize} />
                {showTitle ? <div className="tw-font-medium tw-text-gray-900 tw-text-sm">{platform.label}</div> : null}
              </div>

              {/* Attribution Selection */}
              <div>
                <div className="tw-text-xs tw-text-gray-600 tw-mb-2">Attribution Model</div>
                <div className="tw-flex tw-bg-white tw-rounded tw-p-1 tw-border">
                  <button
                    onClick={() => handleAttributionChange(platform.value, 'first')}
                    disabled={isSaving}
                    className={`tw-flex-1 tw-px-2 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded tw-transition-all ${
                      currentAttribution === 'first'
                        ? 'tw-bg-blue-500 tw-text-white tw-shadow-sm'
                        : 'tw-text-gray-600 hover:tw-text-gray-900 hover:tw-bg-gray-50'
                    } ${isSaving ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}`}
                  >
                    First Visit
                  </button>
                  <button
                    onClick={() => handleAttributionChange(platform.value, 'last')}
                    disabled={isSaving}
                    className={`tw-flex-1 tw-px-2 tw-py-1.5 tw-text-xs tw-font-medium tw-rounded tw-transition-all ${
                      currentAttribution === 'last'
                        ? 'tw-bg-blue-500 tw-text-white tw-shadow-sm'
                        : 'tw-text-gray-600 hover:tw-text-gray-900 hover:tw-bg-gray-50'
                    } ${isSaving ? 'tw-opacity-50 tw-cursor-not-allowed' : ''}`}
                  >
                    Last Visit
                  </button>
                </div>
              </div>
            </div>
          )
        })}
    </div>
  )
}

const MetricsTab = () => {
  const [activeTab, setActiveTab] = useState('metrics')

  const dispatch = useDispatch()

  const metrics = useSelector(selectMetricSettings)

  // const [metrics, setMetrics] = useState({
  //   total_sales: true,
  //   total_sessions: true,
  //   total_orders: true,
  //   total_visitors: true,
  //   total_active_cart: true,
  //   total_checkouts: true,
  // })

  const metricsList = [
    { key: 'total_sales', label: 'Total Sales' },
    { key: 'total_sessions', label: 'Total Sessions' },
    { key: 'total_orders', label: 'Total Orders' },
    { key: 'total_visitors', label: 'Total Visitors' },
    { key: 'total_active_cart', label: 'Total Active Carts' },
    { key: 'total_checkouts', label: 'Total Checkouts' },
    { key: 'total_pageviews', label: 'Total Page Views' },
  ]

  const handleMetricToggle = (metricKey) => {
    dispatch(setMetricSetting({
      tab: activeTab,
      metric: metricKey,
      value: !metrics[activeTab][metricKey]
    }))
  }

  return (
    <>
      <div className="tw-border-b">
        <nav className="tw-flex">
          <button
            onClick={() => setActiveTab('metrics')}
            className={`tw-px-6 tw-py-3 tw-text-sm tw-font-medium tw-border-b-2 tw-transition-colors ${
              activeTab === 'metrics'
                ? 'tw-text-blue-600 tw-border-blue-600'
                : 'tw-text-gray-500 tw-border-transparent hover:tw-text-gray-700 hover:tw-border-gray-300'
            }`}
          >
            Metrics
          </button>
          {/* Add more tabs here if needed */}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="tw-max-h-96 tw-overflow-y-auto">
        {activeTab === 'metrics' && (
          <div>
            <div className="tw-border tw-border-gray-200 tw-rounded-lg tw-overflow-hidden">
              {/* Fixed Header */}
              <div className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                <table className="tw-w-full">
                  <thead>
                    <tr>
                      <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-2/3">
                        Metric
                      </th>
                      <th className="tw-px-4 tw-py-3 tw-text-left tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wider tw-w-1/3">
                        Visible
                      </th>
                    </tr>
                  </thead>
                </table>
              </div>

              {/* Scrollable Body */}
              <div className="tw-max-h-64 tw-overflow-y-auto">
                <table className="tw-w-full">
                  <tbody className="tw-bg-white tw-divide-y tw-divide-gray-200">
                    {metricsList.map((metric) => (
                      <tr key={metric.key} className="hover:tw-bg-gray-50">
                        <td className="tw-px-4 tw-py-4 tw-text-sm tw-text-gray-900 tw-w-2/3">{metric.label}</td>
                        <td className="tw-px-4 tw-py-4 tw-w-1/3">
                          <label className="tw-inline-flex tw-items-center">
                            <input
                              type="checkbox"
                              checked={metrics[activeTab][metric.key]}
                              onChange={() => handleMetricToggle(metric.key)}
                              className="tw-form-checkbox tw-h-4 tw-w-4 tw-text-blue-600 tw-transition tw-duration-150 tw-ease-in-out tw-rounded tw-border-gray-300 focus:tw-ring-blue-500 focus:tw-ring-2"
                            />
                            <span className="tw-ml-2 tw-text-sm tw-text-gray-700">
                              {metrics[activeTab][metric.key] ? 'Show' : 'Hide'}
                            </span>
                          </label>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  )
}

function TrafficSettings({ tab, onSave }) {
  const dispatch = useDispatch()

  const settingsOpen = useSelector(selectSettingsModalOpen)
  const attributionSettings = useSelector(selectAttributionSettings)
  const adPlatformOptions = useSelector(selectAdPlatformOptions)
  const isSaving = useSelector(selectIsSaving)
  const error = useSelector(selectTrafficSettingsError)
  const saveStatus = useSelector(selectSaveStatus)
  const metricSettings = useSelector(selectMetricSettings)

  // Load settings when component mounts
  useEffect(() => {
    dispatch(loadTrafficSettings())
  }, [dispatch])

  // Handle save status changes
  useEffect(() => {
    if (saveStatus === 'fulfilled') {
      console.log('Settings saved successfully!')
      // Reset save status after a delay
      setTimeout(() => dispatch(resetSaveStatus()), 2000)
    } else if (saveStatus === 'rejected') {
      console.error('Failed to save settings:', error)
    }
  }, [saveStatus, error, dispatch])

  const handleOpenChange = (isOpen) => {
    dispatch(setSettingsModalOpen(isOpen))
  }

  const handleCancel = () => {
    dispatch(setSettingsModalOpen(false))
  }

  const handleAttributionChange = (platform, value) => {
    dispatch(setAttributionSetting({ platform, value }))
  }

  const handleSave = async () => {
    try {
      // Dispatch the async thunk to save to database
      if (tab === 'ads') {
        console.log('attributionSettings', attributionSettings)
        await dispatch(saveTrafficSettings({
          settings: attributionSettings,
          tab
        })).unwrap()
      }

      if (tab === 'metrics') {
        console.log('metricSettings', metricSettings)
        await dispatch(saveTrafficSettings({
          settings: metricSettings,
          tab
        })).unwrap()
      }

      // Call the original onSave callback if provided
      // onSave && onSave()

      // Close modal
      dispatch(setSettingsModalOpen(false))

    } catch (error) {
      console.error('Error saving traffic settings:', error)
      // Modal stays open so user can retry
    }
  }

  useEffect(() => {
    if (tab === 'ads' && settingsOpen) {
      // Only set if not already set
      if (!attributionSettings?.google_ads) {
        dispatch(setAttributionSetting({ platform: 'google_ads', value: 'first' }))
      }

      if (!attributionSettings?.facebook_ads) {
        dispatch(setAttributionSetting({ platform: 'facebook_ads', value: 'first' }))
      }

      if (!attributionSettings?.klaviyo) {
        dispatch(setAttributionSetting({ platform: 'klaviyo', value: 'first' }))
      }
    }
  }, [settingsOpen, tab, dispatch, attributionSettings])

  const tabTitles  = tab === 'metrics' ? 'overview' : tab === 'overview' ? 'trends' : tab
  return (
    <Dialog open={settingsOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button
          className="!tw-px-3 !tw-h-9 tw-text-[1rem] !tw-bg-[#0d6efd] hover:!tw-bg-[#0b5ed7] !tw-text-white tw-border-0 tw-shadow-sm"
          data-tip={`${_.upperFirst(tab)} Settings`}
          data-for="settings-tooltip"
        >
          <BsGearFill className="tw-h-[18px] tw-w-[18px] tw-text-white" aria-hidden />
        </Button>
      </DialogTrigger>
      <ReactTooltip id="settings-tooltip" place="bottom" effect="solid" />
      <DialogContent className="tw-sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Traffic Settings</DialogTitle>
          <DialogDescription>Configure settings for the {tabTitles} section.</DialogDescription>
        </DialogHeader>

        {/* Error Display */}
        {error && saveStatus === 'rejected' && (
          <div className="tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-md tw-p-3 tw-mb-4">
            <div className="tw-flex">
              <div className="tw-flex-shrink-0">
                <svg className="tw-h-5 tw-w-5 tw-text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="tw-ml-3">
                <p className="tw-text-sm tw-text-red-800">
                  {error}
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="tw-space-y-4">
          {tab === 'ads' ?  <AdsSettings isSaving={isSaving} adPlatformOptions={adPlatformOptions} handleAttributionChange={handleAttributionChange} attributionSettings={attributionSettings} /> : null}
          {tab === 'metrics' ? <MetricsTab /> : null}
          {!['ads', 'metrics'].includes(tab) ? (
            <div className="tw-text-center tw-py-8">
              <div className="tw-w-12 tw-h-12 tw-bg-gray-100 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-3">
                <Settings className="tw-w-6 tw-h-6 tw-text-gray-400" />
              </div>
              <p className="tw-text-gray-500 tw-text-sm">Settings for {tabTitles} section are coming soon...</p>
            </div>
          ) : null}
        </div>

        <div className="tw-flex tw-justify-end tw-space-x-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleSave}
            disabled={isSaving}
            className="tw-min-w-[120px]"
          >
            {isSaving ? (
              <div className="tw-flex tw-items-center tw-gap-2">
                <svg className="tw-animate-spin tw-h-4 tw-w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </div>
            ) : (
              'Save Settings'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default TrafficSettings